/**
 * **The booking policy as the Ustawienia tab reads and changes it** (booking-system R1, R12; T-B1).
 *
 * The tab draws the mobile settings panel's rows — a title, one sentence of what the setting DOES,
 * and one control — in the canon's two groups; every row's words and reading are decided here, as
 * mobile's `policyForm` decides them, so the component composes nothing.
 *
 * ── One change, one PATCH, carrying only that field ──────────────────────────────────────────
 * `PATCH /booking-policy` leaves an omitted field as it stands (`PatchBookingPolicyRequest`,
 * `BookingPolicyProcessor.ApplyPatch`), so a change sends the one field that changed and nothing
 * else — never a diff of two policies. The one wire subtlety is the slot step: `null` on the READ
 * means „co długość sesji", but a PATCH cannot say `null` (an omitted field is silence), so going
 * back to it is `slotStepFollowsLength: true`, which clears the step — and the two may never travel
 * together (`RequireCoherentSlotStep`). {@link policyPatch} is the one place that spelling happens.
 *
 * ── Two kinds of list ────────────────────────────────────────────────────────────────────────
 * R1 LISTS the lengths, the step and the buffer: a value off them is refused
 * (`booking_value_not_allowed`), so those ladders ARE the server's lists. The notice, the horizon
 * and the cancellation window it only BOUNDS; their ladders are mobile's rungs inside the bounds
 * (R1 names them), and a stored value off a rung is shown as a rung of its own rather than snapped —
 * a settings screen that cannot show what is stored is lying about it.
 *
 * Pure: no React, no DOM, no query hook, no i18next — `t` and the locale arrive as parameters.
 */

import type { TFunction } from 'i18next';

import type {
  TrainWithMeModulesBookingDtosRequestsPatchBookingPolicyRequest as PatchBookingPolicyRequest,
  TrainWithMeModulesBookingDtosResponsesBookingPolicyResponse as BookingPolicy,
} from '~/shared/api/generated/model';

import { dayMonthWords, weekdayDayMonthClock } from './calendarWords';
import { shiftDay, type CivilDay } from './trainerClock';

export type PolicyPickField =
  | 'trainingMinutes'
  | 'consultationMinutes'
  | 'slotStep'
  | 'bufferMinutes'
  | 'minimumNoticeHours'
  | 'horizonDays'
  | 'cancellationWindowHours';

export type PolicyToggleField = 'selfBookingEnabled' | 'customerCancellationAllowed';

export type PolicyField = PolicyPickField | PolicyToggleField;

/** A pick's value. `null` is only ever the slot step's „co długość sesji". */
export type PickValue = number | null;

// ── R1's LISTS: exactly the server's. ───────────────────────────────────────────────────────
const SESSION_LENGTHS = [30, 45, 60, 90] as const;
const SLOT_STEPS = [15, 30, 60] as const;
const BUFFERS = [0, 5, 10, 15] as const;
// ── R1's BOUNDS, as mobile's rungs inside them (R1 names these ladders). ─────────────────────
const NOTICE_HOURS = [0, 2, 4, 6, 12, 24, 48, 72, 168] as const;
const HORIZON_DAYS = [7, 14, 30, 60, 90] as const;
const CANCELLATION_HOURS = [0, 2, 4, 6, 12, 24, 48, 72] as const;

export type SettingsRow =
  | {
      readonly kind: 'toggle';
      readonly id: PolicyToggleField;
      readonly title: string;
      readonly effect: string;
      readonly value: boolean;
    }
  | {
      readonly kind: 'pick';
      readonly id: PolicyPickField;
      readonly title: string;
      readonly effect: string | null;
      /** The pill's reading — `60 min`, `12 h`, `co długość sesji`. */
      readonly value: string;
      /** A row whose setting has no effect right now reads quiet (and says why in its effect). */
      readonly enabled: boolean;
    }
  | {
      readonly kind: 'schedule';
      readonly title: string;
      readonly effect: string;
      /** `46 h tygodniowo`, or `null` when the hours did not answer. */
      readonly hours: string | null;
    };

export interface SettingsGroup {
  readonly id: 'booking' | 'cancellation';
  readonly title: string;
  readonly rows: readonly SettingsRow[];
}

export interface SettingsContext {
  readonly nowMs: number;
  readonly zone: string;
  readonly today: CivilDay;
  readonly locale: string;
  /** Minutes a week of the pattern works — the „Kiedy pracuję" row's reading; `null` unread. */
  readonly weekMinutes: number | null;
}

export interface PickOption {
  readonly value: PickValue;
  readonly label: string;
  readonly selected: boolean;
}

export interface PolicyPick {
  readonly title: string;
  readonly options: readonly PickOption[];
  /** The one line under the chips: what the CURRENT value means today, where that is a moment. */
  readonly footer: string | null;
}

const KEY = 'web:booking.trainer';

/**
 * What a trainer who never opened the settings behaves as (`BookingDefaults`, R1). The tab draws
 * its WAIT in this policy's shape — its rows and the line boxes of their sentences, the values and
 * sentences themselves hidden under bars — so the answer landing changes ink, not geometry.
 */
export const DEFAULT_POLICY: BookingPolicy = {
  selfBookingEnabled: false,
  trainingMinutes: 60,
  consultationMinutes: 45,
  slotStepMinutes: null,
  effectiveTrainingStepMinutes: 60,
  effectiveConsultationStepMinutes: 15,
  bufferMinutes: 0,
  minimumNoticeHours: 12,
  horizonDays: 30,
  customerCancellationAllowed: true,
  cancellationWindowHours: 24,
  creditsEnabled: false,
};

/**
 * The week the wait draws „Kiedy pracuję"'s total for, under a bar. The total stands beside the
 * row's chevron and takes width from the row's sentence, so a wait drawn without it wraps that
 * sentence differently from the answer — at 1280 wide the row grew 16px when the hours landed.
 * Forty hours is two digits, what most weeks are; the digits are tabular, so any two-digit total
 * takes the same width.
 */
export const DEFAULT_WEEK_MINUTES = 40 * 60;

/**
 * R1's step for a session type when none is named: the length itself where it is one of the three
 * steps, otherwise the largest of them that divides it (45 steps by 15, 90 by 30).
 */
export function effectiveStep(lengthMinutes: number, stepMinutes: number | null): number {
  if (stepMinutes !== null) return stepMinutes;
  if ((SLOT_STEPS as readonly number[]).includes(lengthMinutes)) return lengthMinutes;
  return [...SLOT_STEPS].reverse().find((step) => lengthMinutes % step === 0) ?? SLOT_STEPS[0];
}

/** `46 h` or `7,5 h` — a week's working minutes as the cards write them. */
export function weekHoursLabel(minutes: number, locale: string): string {
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours.toLocaleString(locale)} h`;
}

function minutesLabel(minutes: number, t: TFunction): string {
  return t(`${KEY}.value.minutes`, { count: minutes });
}

function pickLabel(field: PolicyPickField, value: PickValue, t: TFunction): string {
  if (value === null) return t(`${KEY}.value.stepFollowsLength`);
  switch (field) {
    case 'minimumNoticeHours':
      return t(`${KEY}.value.hours`, { count: value });
    case 'cancellationWindowHours':
      return t(`${KEY}.value.hoursBefore`, { count: value });
    case 'horizonDays':
      return t(`${KEY}.value.days`, { count: value });
    default:
      return minutesLabel(value, t);
  }
}

/** What the policy says for a FORM field — the step's `null` is a value, not an absence. */
export function pickValueOf(policy: BookingPolicy, field: PolicyPickField): PickValue {
  switch (field) {
    case 'slotStep':
      return policy.slotStepMinutes ?? null;
    default:
      return policy[field] ?? null;
  }
}

function noticeEffect(hours: number, t: TFunction): string {
  return hours === 0
    ? t(`${KEY}.settings.rows.minimumNoticeNone.effect`)
    : t(`${KEY}.settings.rows.minimumNoticeHours.effect`, { count: hours });
}

function horizonEffect(days: number, context: SettingsContext, t: TFunction): string {
  return t(`${KEY}.settings.rows.horizonDays.effect`, {
    date: dayMonthWords(shiftDay(context.today, days), context.locale),
  });
}

/**
 * „Bezpłatne odwołanie do" says one of two things: with client cancellation off it is inert and
 * says what brings it to life (and goes quiet), otherwise it says what happens after the window. It
 * names a credit only while the trainer runs credits — a trainer settling in cash has no token to
 * forfeit.
 */
function cancellationEffect(policy: BookingPolicy, t: TFunction): string {
  if (policy.customerCancellationAllowed !== true) {
    return t(`${KEY}.settings.rows.cancellationWindowHours.inactiveEffect`);
  }
  return t(
    policy.creditsEnabled === true
      ? `${KEY}.settings.rows.cancellationWindowHours.effectCredits`
      : `${KEY}.settings.rows.cancellationWindowHours.effect`,
  );
}

function pickRow(
  policy: BookingPolicy,
  id: PolicyPickField,
  effect: string | null,
  t: TFunction,
  enabled = true,
): SettingsRow {
  return {
    kind: 'pick',
    id,
    title: t(`${KEY}.settings.rows.${id}.title`),
    effect,
    value: pickLabel(id, pickValueOf(policy, id), t),
    enabled,
  };
}

function toggleRow(policy: BookingPolicy, id: PolicyToggleField, t: TFunction): SettingsRow {
  return {
    kind: 'toggle',
    id,
    title: t(`${KEY}.settings.rows.${id}.title`),
    effect: t(`${KEY}.settings.rows.${id}.effect`),
    value: policy[id] === true,
  };
}

/** The whole tab as data: the canon's two groups, in its order, each row worded and read. */
export function policySettingsView(
  policy: BookingPolicy,
  context: SettingsContext,
  t: TFunction,
): readonly SettingsGroup[] {
  const training = policy.trainingMinutes ?? 0;
  const consultation = policy.consultationMinutes ?? 0;
  return [
    {
      id: 'booking',
      title: t('web:booking.settings.groupBooking'),
      rows: [
        toggleRow(policy, 'selfBookingEnabled', t),
        pickRow(policy, 'trainingMinutes', t(`${KEY}.settings.rows.trainingMinutes.effect`), t),
        // The canon gives consultation the same sentence; mobile's row has none, the web draws it.
        pickRow(policy, 'consultationMinutes', t(`${KEY}.settings.rows.trainingMinutes.effect`), t),
        pickRow(
          policy,
          'slotStep',
          t('web:booking.settings.stepEffect', {
            training:
              policy.effectiveTrainingStepMinutes ??
              effectiveStep(training, policy.slotStepMinutes ?? null),
            consultation:
              policy.effectiveConsultationStepMinutes ??
              effectiveStep(consultation, policy.slotStepMinutes ?? null),
          }),
          t,
        ),
        pickRow(policy, 'bufferMinutes', t(`${KEY}.settings.rows.bufferMinutes.effect`), t),
        pickRow(policy, 'minimumNoticeHours', noticeEffect(policy.minimumNoticeHours ?? 0, t), t),
        pickRow(policy, 'horizonDays', horizonEffect(policy.horizonDays ?? 0, context, t), t),
        {
          kind: 'schedule',
          title: t(`${KEY}.settings.rows.schedule.title`),
          effect: t(`${KEY}.settings.rows.schedule.effect`),
          hours:
            context.weekMinutes === null
              ? null
              : t('web:booking.settings.weekHours', {
                  hours: weekHoursLabel(context.weekMinutes, context.locale),
                }),
        },
      ],
    },
    {
      id: 'cancellation',
      title: t(`${KEY}.settings.groups.cancellation`),
      rows: [
        toggleRow(policy, 'customerCancellationAllowed', t),
        pickRow(
          policy,
          'cancellationWindowHours',
          cancellationEffect(policy, t),
          t,
          // R12 freezes the window on each booking, so the value still matters — but with client
          // cancellation off no client meets it. Quiet, not gone: switching back on reads it.
          policy.customerCancellationAllowed === true,
        ),
      ],
    },
  ];
}

function ladderOf(field: PolicyPickField): readonly PickValue[] {
  switch (field) {
    case 'trainingMinutes':
    case 'consultationMinutes':
      return SESSION_LENGTHS;
    case 'slotStep':
      return [null, ...SLOT_STEPS];
    case 'bufferMinutes':
      return BUFFERS;
    case 'minimumNoticeHours':
      return NOTICE_HOURS;
    case 'horizonDays':
      return HORIZON_DAYS;
    case 'cancellationWindowHours':
      return CANCELLATION_HOURS;
  }
}

/** One field's chips: its ladder (plus a stored value off it, in order) and the line under them. */
export function policyPick(
  policy: BookingPolicy,
  field: PolicyPickField,
  context: SettingsContext,
  t: TFunction,
): PolicyPick {
  const current = pickValueOf(policy, field);
  const ladder = ladderOf(field);
  const values =
    current === null || ladder.includes(current)
      ? ladder
      : [...ladder, current].sort((a, b) => (a ?? -1) - (b ?? -1));
  const footer =
    field === 'minimumNoticeHours' && current !== null
      ? t(`${KEY}.pick.minimumNoticeHours.sub`, {
          date: weekdayDayMonthClock(
            context.nowMs + current * 3_600_000,
            context.zone,
            context.locale,
          ),
        })
      : field === 'horizonDays' && current !== null
        ? horizonEffect(current, context, t)
        : null;
  return {
    title: t(`${KEY}.pick.${field}.title`),
    options: values.map((value) => ({
      value,
      label: pickLabel(field, value, t),
      selected: value === current,
    })),
    footer,
  };
}

/** What the policy says for any field the tab changes — a pick's value, or a switch's state. */
export function policyFieldValue(policy: BookingPolicy, field: PolicyField): PickValue | boolean {
  return field === 'selfBookingEnabled' || field === 'customerCancellationAllowed'
    ? policy[field] === true
    : pickValueOf(policy, field);
}

/** The one-field body a change sends. The step's „co długość sesji" is its own spelling. */
export function policyPatch(
  field: PolicyField,
  value: PickValue | boolean,
): PatchBookingPolicyRequest {
  if (field === 'slotStep') {
    return value === null ? { slotStepFollowsLength: true } : { slotStepMinutes: value as number };
  }
  return { [field]: value };
}

/**
 * The policy as it will read once the change lands — what the tab draws while the PATCH is out.
 * The effective steps are recomputed by R1's own rule, so the step row's sentence follows too.
 */
export function policyWithChange(
  policy: BookingPolicy,
  field: PolicyField,
  value: PickValue | boolean,
): BookingPolicy {
  const next: BookingPolicy =
    field === 'slotStep'
      ? { ...policy, slotStepMinutes: value as number | null }
      : { ...policy, [field]: value };
  const step = next.slotStepMinutes ?? null;
  return {
    ...next,
    effectiveTrainingStepMinutes: effectiveStep(next.trainingMinutes ?? 0, step),
    effectiveConsultationStepMinutes: effectiveStep(next.consultationMinutes ?? 0, step),
  };
}
